import React, { useEffect, useState } from 'react';
import {
  Stack,
  Grid,
  Box,
  Typography,
  CardHeader,
  IconButton,
  Card,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import Datepicker from '../form-elements/datepicker.component';
import { getSummary } from '../../services/reports.service';
import { formatDate, getDate } from '../../utils/datetime.util';
import PLATFORMS_LIST, { CHART_COLORS } from '../../config/platforms.config';
import SearchableChannel from '../form-elements/searchableChannel.component';
import { CONTENT_ALIAS, DEFAULT_CHANNEL } from '../../config/const.config';
import { getSearchListChannels } from '../../services/channel.service';
import Iconify from '../common/iconify.component';

const PieChart = () => {
  const [seriesData, setSeriesData] = useState([]);
  const [xLabels, setXLabels] = useState([]);
  const [sessionPCount, setSessionPCount] = useState([]);
  const [colors, setColors] = useState([]);

  const [filters, setFilters] = useState({
    channels: [],
    startDate: getDate(-7),
    endDate: getDate(0),
    reloadCounter: 0,
    loading: true,
  });

  const optionsSeries = {
    series: seriesData,
    labels: xLabels,
    colors,
    legend: {
      position: 'bottom',
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  };

  const handleStartDateChange = (v) => {
    if (filters.loading) return;
    setFilters({
      ...filters,
      startDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };

  const handleEndDateChange = (v) => {
    if (filters.loading) return;
    setFilters({
      ...filters,
      endDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };

  const loadChannelsOptions = (params, callBack) => {
    getSearchListChannels(params).then((res) => {
      callBack(res.data);
    });
  };

  const handleChannelChange = (v) => {
    setFilters({
      ...filters,
      channels: v,
      reloadCounter: filters.reloadCounter + 1,
    });
  };

  const handleRefreshData = () => {
    setFilters({
      ...filters,
      reloadCounter: filters.reloadCounter + 1,
    });
  };

  useEffect(() => {
    const payload = {};
    const startDate = formatDate(filters.startDate, 'YYYY-MM-DD');
    payload.startDate = startDate;
    const endDate = formatDate(filters.endDate, 'YYYY-MM-DD');
    payload.endDate = endDate;
    if (filters.channels?.length > 0) {
      const channels = [];
      filters.channels.forEach((c) => channels.push(c.id));
      payload.channels = channels;
    }

    getSummary(payload)
      .then((res) => {
        const le = [];
        const platform = [];
        const sC = [];
        const colorMap = [];

        res.data.forEach((pd) => {
          const platformData = PLATFORMS_LIST.find((p) => p.id === pd.platform);
          le.push(parseFloat(pd.durationPercentage));
          platform.push(platformData.title);
          sC.push(parseFloat(pd.sessionPercentageCount));
          if (!colorMap.includes(CHART_COLORS[pd.platform.toUpperCase()])) {
            colorMap.push(CHART_COLORS[pd.platform.toUpperCase()]); // Assign colors based on platform
          }
        });

        setSeriesData(le);
        setSessionPCount(sC);
        setXLabels(platform);
        setColors(colorMap); // Set the dynamic colors for the chart

        setFilters({ ...filters, loading: false });
      })
      .catch(() => {
        setFilters({ ...filters, loading: false });
      });
  }, [filters.reloadCounter]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Video Play Duration and Session Count Summary"
        subheader="Video play duration and session count summary per platform."
        action={
          <IconButton title="Refresh" onClick={handleRefreshData}>
            <Iconify icon="ic:twotone-refresh" />
          </IconButton>
        }
      />

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <Grid container alignItems="center">
          {!DEFAULT_CHANNEL && (
            <Grid item xs={6}>
              <SearchableChannel
                id="channels"
                name="channels"
                label={`Select ${CONTENT_ALIAS.CHANNEL_PLURAL}`}
                multiple
                defaultValue={filters.channels}
                onChange={handleChannelChange}
                sx={{ width: '100%' }}
                loadOption={loadChannelsOptions}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <Stack direction="row" justifyContent="right" gap={2}>
              <Datepicker
                id="dr_start_date"
                name="dr_start_date"
                label="Start Date"
                defaultValue={filters.startDate}
                onChange={handleStartDateChange}
                sx={{ marginRight: 2, width: 200 }}
              />
              <Datepicker
                id="dr_end_date"
                name="dr_end_date"
                label="End Date"
                defaultValue={filters.endDate}
                onChange={handleEndDateChange}
                sx={{ width: 200 }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {seriesData.length <= 0 && (
              <Grid
                direction="row"
                maxWidth="lg"
                height={400}
                justifyContent="center"
                alignItems="center"
                display="flex"
              >
                <Typography style={{ fontWeight: 700 }}>
                  No reports available for Video play duration and session count
                  summary per platform.
                </Typography>
              </Grid>
            )}
            {seriesData.length > 0 && (
              <Grid container item xs={12} justifyContent="center">
                <Grid item xs={6} sx={{ marginTop: 2 }}>
                  <ReactApexChart
                    type="pie"
                    series={seriesData}
                    options={optionsSeries}
                    height={350}
                  />
                  <Typography
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      marginTop: 16,
                    }}
                  >
                    Video Play Duration Summary per Platform
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ marginTop: 2 }}>
                  <ReactApexChart
                    type="pie"
                    series={sessionPCount}
                    options={optionsSeries}
                    height={350}
                  />
                  <Typography
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      marginTop: 16,
                    }}
                  >
                    Video Play Session Count Summary per Platform
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default PieChart;
